import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';
import { GoogleIcon } from '../components/GoogleIcon';

function safeNext(raw: string | null, fallback: string): string {
  if (!raw) return fallback;
  if (!raw.startsWith('/') || raw.startsWith('//')) return fallback;
  return raw;
}

export function SignUpPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const [search] = useSearchParams();
  const next = safeNext(search.get('next'), '/dashboard');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmSent, setConfirmSent] = useState(false);

  if (auth.user) {
    navigate(next, { replace: true });
    return null;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (password.length < 8) {
      setErr('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(next)}`,
      },
    });
    setLoading(false);
    if (error) {
      setErr(error.message);
      return;
    }
    if (data.session) {
      navigate(next, { replace: true });
    } else {
      setConfirmSent(true);
    }
  }

  async function onGoogle() {
    setErr(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(next)}`,
        queryParams: { prompt: 'select_account' },
      },
    });
    if (error) setErr(error.message);
  }

  return (
    <div className="max-w-md mx-auto px-6 py-16 md:py-20">
      <div className="eyebrow mb-4">Create your account</div>
      <h1 className="editorial-h1 mb-3">
        Get <em className="italic text-accent">started.</em>
      </h1>
      <p className="editorial-deck mb-10">
        Free. No credit card. We'll set up your LEA workspace on the next screen.
      </p>

      <button
        type="button"
        onClick={onGoogle}
        className="w-full flex items-center justify-center gap-3 py-3 px-4 border border-ink text-ink text-sm font-label hover:bg-paper-deep transition-colors mb-3"
      >
        <GoogleIcon />
        Sign up with Google
      </button>

      <div className="flex items-center gap-3 my-7">
        <div className="flex-1 h-px bg-rule" />
        <span className="font-label text-[10px] tracking-[0.2em] uppercase text-ink-muted">
          or with email
        </span>
        <div className="flex-1 h-px bg-rule" />
      </div>

      {confirmSent ? (
        <div className="border-l-2 border-accent pl-5 py-4 font-editorial italic text-[16px] text-ink-soft leading-relaxed">
          Confirm your email — we sent a link to{' '}
          <strong className="font-medium not-italic text-ink">{email}</strong>. Open
          it on this device to finish signing up.
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <Field
            label="Your name"
            value={fullName}
            onChange={setFullName}
            autoComplete="name"
          />
          <Field
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            autoComplete="email"
          />
          <Field
            label="Password"
            type="password"
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
          />
          {err && (
            <div className="font-editorial italic text-[14px] text-[#a02525]">
              ⚠ {err}
            </div>
          )}

          <div className="pt-2 flex justify-end">
            <button type="submit" disabled={loading} className="btn-text">
              {loading ? 'Creating account…' : 'Create account'}
            </button>
          </div>
        </form>
      )}

      <div className="mt-10 pt-6 border-t border-rule font-editorial italic text-[15px] text-ink-muted">
        Already have an account?{' '}
        <Link
          to={`/signin${next !== '/dashboard' ? `?next=${encodeURIComponent(next)}` : ''}`}
          className="text-link underline underline-offset-2 hover:text-accent not-italic"
        >
          Sign in
        </Link>
      </div>
    </div>
  );
}

function Field({
  label,
  type = 'text',
  value,
  onChange,
  autoComplete,
}: {
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
}) {
  return (
    <label className="block">
      <span className="font-label text-[10px] font-medium uppercase tracking-[0.18em] text-ink-muted block mb-1.5">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        required
        className="w-full bg-transparent border-0 border-b border-rule focus:border-accent focus:outline-none font-editorial text-[17px] text-ink py-2"
      />
    </label>
  );
}
